/**
 * Copyright Mántum All Rights Reserved.
 */
import { createContext, useContext, useReducer } from 'react';

import type { Dispatch } from 'react';

export const EVENT_CHANGED = 'changed';
export const EVENT_CLEAR = 'clear';
export const EVENT_UNMOUNT = 'unmount';

export const ACTION_DATASOURCE = 'datasource';
export const ACTION_VALUE = 'value';

type Action = typeof ACTION_VALUE | typeof ACTION_DATASOURCE;

type Event =
    | {
          content: Content[];
          type: typeof EVENT_CHANGED;
          merge?: boolean;
      }
    | {
          content: ContentClear[];
          type: typeof EVENT_CLEAR;
          merge?: boolean;
      }
    | {
          content: string;
          type: typeof EVENT_UNMOUNT;
          merge?: boolean;
      };

type State = Record<
    string,
    {
        action: Action;
        clear?: boolean;
        origin?: string;
        previous?: boolean;
        value: unknown;
    }
>;

export type Content = {
    action: Action;
    clear?: boolean;
    origin?: string;
    previous?: boolean;
    target: string;
    value: unknown;
};

export type ContentClear = {
    action: Action;
    origin?: string;
    target: string;
};

function initialState() {
    // do nothing.
}

const GroupContext = createContext<State | null>(null);
const GroupDispatchContext = createContext<Dispatch<Event>>(initialState);

type PropTypes = {
    children?: React.ReactNode;
};

export default function Group(props: PropTypes) {
    const [groups, dispatch] = useReducer(groupReducer, {});

    return (
        <GroupContext.Provider value={groups}>
            <GroupDispatchContext.Provider value={dispatch}>
                {props.children}
            </GroupDispatchContext.Provider>
        </GroupContext.Provider>
    );
}

export function useGroup() {
    return useContext(GroupContext);
}

export function useGroupDispatch() {
    return useContext(GroupDispatchContext);
}

function groupReducer(state: State | null, event: Event): State | null {
    if (event.type === EVENT_CLEAR) {
        return {
            ...state,
            ...event.content.reduce(
                (previous, current) => ({
                    ...previous,
                    [current.target]: {
                        action: current.action,
                        origin: current.origin,
                        value: ''
                    }
                }),
                {}
            )
        };
    }

    if (event.type === EVENT_CHANGED) {
        if (event.merge) {
            return {
                ...state,
                ...event.content.reduce(
                    (previous, current) => ({
                        ...previous,
                        [current.target]: {
                            action: current.action,
                            clear: current.clear === undefined || current.clear,
                            origin: current.origin,
                            previous: current.previous,
                            value: current.value
                        }
                    }),
                    {}
                )
            };
        }

        return event.content.reduce(
            (previous, current) => ({
                ...previous,
                [current.target]: {
                    action: current.action,
                    clear: current.clear === undefined || current.clear,
                    origin: current.origin,
                    previous: current.previous,
                    value: current.value
                }
            }),
            {}
        );
    }

    if (event.type === EVENT_UNMOUNT) {
        if (state && event.content in state) {
            delete state[event.content];
        }
    }

    return state;
}
